import _ from "lodash";
import { DateTime } from "luxon";
import type { Layout, PlotData } from "plotly.js";
import React, { useMemo } from "react";
import { Segment } from "semantic-ui-react";
import { AucTableData } from "../../../../apis/types/analysisTypes";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { getColorFromPalette } from "../../Trials/MFASummary/MapStyles";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import { useSelector } from "react-redux";
import { AXIS_TICK_SIZE, AXIS_TITLE_SIZE, CHART_TITLE_SIZE } from "./constants";

type Props = {
	aucData?: AucTableData[];
	analysisName: string;
	gduList?: { day: string; gdu: number; monthDay: string }[];
	flights?: { flightId: string; flightDate: string; isPlantFlight: boolean; isHarvestFlight: boolean }[]; // assumed this comes in sorted
};

const TimecourseLineChart = ({ aucData, analysisName, gduList, flights }: Props) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);

	const {
		committed: { useGdu },
		pending: { selectedTreatmentMatrix, selectedFlightIds },
		display: { collapseTable },
	} = useTrialAnalysis();

	const { changeSettings } = useTrialAnalysisDispatch();

	//-- Data
	const chartData = useMemo(() => {
		if (!flights || !selectedFlightIds || !selectedTreatmentMatrix || !aucData?.length) return undefined;

		const traces = _.chain(aucData)
			.map((ad) => {
				const points = _.chain(flights)
					.filter((f) => selectedFlightIds.includes(f.flightId))
					.map((f) => {
						const pfv = ad.perFlightValues[f.flightId];

						const gdu = gduList
							?.slice(
								0,
								gduList?.findIndex((item) => DateTime.fromISO(item.day).hasSame(DateTime.fromISO(f.flightDate), "day")),
							)
							.reduce((a, b) => a + b.gdu, 0)
							.toFixed(0);

						const x = useGdu && gdu ? Number(gdu) : DateTime.fromISO(f.flightDate).toLocal().toJSDate();

						const point = {
							x: x,
							y: pfv,
							label: useGdu && gdu ? gdu : DateTime.fromISO(f.flightDate).toLocal().toFormat("L/d"),
						};

						return point;
					})
					.value();

				const trace: Partial<PlotData> = {
					x: _.map(points, "x"),
					y: _.map(points, "y"),
					text: _.map(points, "label"),
					marker: {
						color: getColorFromPalette(ad.trialTreatmentId - 1) + "80",
						line: {
							color: getColorFromPalette(ad.trialTreatmentId - 1),
							width: 2,
						},
					},
					name: ad.trialTreatmentId.toString(),
					type: "scatter",
					mode: "lines+markers",
					hovertemplate: `${ad.trialTreatmentId} - %{text}: %{y}<extra></extra>`,
					visible: selectedTreatmentMatrix[ad.trialTreatmentId] ? true : collapseTable ? false : "legendonly",
				};
				return trace;
			})
			.sortBy((t) => Number(t.name))
			.value();

		return traces;
	}, [flights, gduList, selectedFlightIds, aucData, selectedTreatmentMatrix, useGdu, collapseTable]);

	const chartLayout: Partial<Layout> = {
		showlegend: true,
		legend: {
			orientation: "h",
			xanchor: "center",
			x: 0.5,
			y: -0.2,
			yanchor: "top",
		},
		height: 400,
		autosize: true,
		clickmode: "none",
		margin: { b: 80, l: 60, r: 0, t: 60, pad: 0 },
		title: { text: "Time Series", x: 0.01, xanchor: "left", font: { size: CHART_TITLE_SIZE } },
		xaxis: {
			showgrid: false,
			tickformat: !useGdu ? "%-m/%-d" : undefined,
			autorange: true,
			rangemode: "tozero",
			automargin: true,
			tickfont: { size: AXIS_TICK_SIZE },
		},
		yaxis: {
			autorange: true,
			rangemode: "tozero",
			automargin: true,
			tickfont: { size: AXIS_TICK_SIZE },
			title: {
				text: analysisName,
				font: { size: AXIS_TITLE_SIZE },
			},
		},
		paper_bgcolor: "#fff0",
		plot_bgcolor: "#fff0",
	};

	const snapshotFileName = [entityNames?.trialName, /\(([^)]+)\)/.exec(analysisName)?.[1] ?? analysisName, "LineChart"]
		.filter(Boolean)
		.join("_");

	const chartConfig = {
		displayModeBar: true,
		scrollZoom: false,
		dragMode: false,
		responsive: true,
		toImageButtonOptions: {
			filename: snapshotFileName,
		},
	};

	return (
		<Segment basic style={{ borderRadius: "8px", border: "1px solid var(--border)", background: "#ffffffe6" }}>
			{!chartData ? (
				<p> There is no data selected for the line chart.</p>
			) : (
				<AFPlot
					data={chartData}
					layout={chartLayout}
					config={chartConfig}
					useResizeHandler
					style={{ width: "100%", height: "400px" }}
					onLegendClick={(e) => {
						if (!selectedTreatmentMatrix) return false;

						const index = Number(e.data[e.curveNumber].name!);

						const newMatrix = selectedTreatmentMatrix.map((t) => t);
						newMatrix[index] = !newMatrix[index];
						changeSettings({ selectedTreatmentMatrix: newMatrix });
						return false;
					}}
					onLegendDoubleClick={(e) => {
						if (!selectedTreatmentMatrix) return false;

						const index = Number(e.data[e.curveNumber].name!);
						if (!selectedTreatmentMatrix.reduce((acc, curr) => acc || curr, false)) {
							const newMatrix = selectedTreatmentMatrix.map(() => true);
							changeSettings({ selectedTreatmentMatrix: newMatrix });
						} else {
							const newMatrix = selectedTreatmentMatrix.map((_, i) => i === index);
							changeSettings({ selectedTreatmentMatrix: newMatrix });
						}

						return false;
					}}
				/>
			)}
		</Segment>
	);
};

export default TimecourseLineChart;
